import { ReactiveController, ReactiveControllerHost } from 'lit';
import { authService } from '../services/auth-service';
import { supabase } from '../services/supabase';
import type { AuthState } from '../contexts/auth-context';
import type { UserRole } from '../types/database';

export class AuthController implements ReactiveController {
  private host: ReactiveControllerHost;
  private unsubscribe?: () => void;

  authState: AuthState = {
    user: null,
    session: null,
    role: null,
    loading: true,
    error: null,
  };

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  async hostConnected() {
    // Initialize auth state
    await this.initializeAuth();

    // Listen to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        this.authState.session = session;
        this.authState.user = session?.user ?? null;

        if (session?.user) {
          await this.fetchUserRole(session.user.id);
        } else {
          this.authState.role = null;
        }

        this.authState.loading = false;
        this.host.requestUpdate();
      }
    );

    this.unsubscribe = () => subscription.unsubscribe();
  }

  hostDisconnected() {
    this.unsubscribe?.();
  }

  private async initializeAuth() {
    try {
      const session = await authService.getSession();
      this.authState.session = session;
      this.authState.user = session?.user ?? null;

      if (session?.user) {
        await this.fetchUserRole(session.user.id);
      }
    } catch (error) {
      this.authState.error = error instanceof Error ? error.message : 'فشل التحقق من الجلسة';
    } finally {
      this.authState.loading = false;
      this.host.requestUpdate();
    }
  }

  private async fetchUserRole(userId: string) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single();

      if (error) throw error;
      this.authState.role = (data as any)?.role as UserRole;
    } catch (error) {
      console.error('Error fetching user role:', error);
      this.authState.role = null;
    }
  }

  async login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    console.log('🎮 AuthController.login called', { email });
    this.authState.loading = true;
    this.authState.error = null;
    this.host.requestUpdate();

    console.log('📡 Calling authService.signIn...');
    const { error, user } = await authService.signIn(email, password);

    console.log('🔍 SignIn response:', { error: error?.message, user: user?.email });

    if (error) {
      console.log('❌ Login error in controller:', error.message);
      this.authState.error = error.message;
      this.authState.loading = false;
      this.host.requestUpdate();
      return { success: false, error: error.message };
    }

    console.log('✅ Login successful in controller');
    return { success: true };
  }

  async signup(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    this.authState.loading = true;
    this.authState.error = null;
    this.host.requestUpdate();

    const { error } = await authService.signUp(email, password);

    if (error) {
      this.authState.error = error.message;
      this.authState.loading = false;
      this.host.requestUpdate();
      return { success: false, error: error.message };
    }

    return { success: true };
  }

  async logout(): Promise<void> {
    await authService.signOut();
    this.authState = {
      user: null,
      session: null,
      role: null,
      loading: false,
      error: null,
    };
    this.host.requestUpdate();
  }
}
